import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Components
import { IconSearch } from '@u21/tabler-icons';
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';

// Actions
import { txnEventsSearch } from 'app/modules/search/actions';

// Selectors
import {
  selectTxnEventsSearchResults,
  selectTxnEventsSearchLoading,
} from 'app/modules/search/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Utils

// Helpers
import { formatTransactionOptions } from 'app/modules/search/helpers';
import { selectSearchV2Enabled } from 'app/shared/featureFlags/selectors';
import {
  MatchType,
  TransactionEventSearchOptions,
} from 'app/modules/search/enum';
import {
  searchTransaction,
  useTransactionSearch,
} from 'app/modules/transactions/queries/useTransactionSearch';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionSearchPayload } from 'app/modules/search/payload';
import { TRANSACTION_QUERY_KEYS } from 'app/modules/transactions/queries/keys';
import {
  selectAgentOrgID,
  selectHasReadEventsPermission,
} from 'app/modules/session/selectors';
import { TxnEventExternalId } from 'app/shared/types/eventExternalIds';

export type U21TransactionEventSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<TxnEventExternalId, TClearable>,
    'options' | 'onSearch'
  >;

export const U21TransactionEventSearch = <TClearable extends boolean>(
  props: U21TransactionEventSearchProps<TClearable>,
) => {
  const { multi, value } = props;

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [newPayload, setNewPayload] = useState<TransactionSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const { data: v1Data } = useSelector(selectTxnEventsSearchResults);
  const v1Loading = useSelector(selectTxnEventsSearchLoading);
  const hasReadEventsPermissions = useSelector(selectHasReadEventsPermission);

  const isV2SearchUsed = useMemo(
    () => Boolean(isSearchV2Enabled),
    [isSearchV2Enabled],
  );

  const {
    refreshTransactions,
    transactions,
    query: { isFetching, isError: isV2Error },
  } = useTransactionSearch(newPayload, isV2SearchUsed);

  const data = useMemo(
    () => (isV2SearchUsed ? transactions : v1Data),
    [transactions, isV2SearchUsed, v1Data],
  );

  const loading = useMemo(
    () => (isV2SearchUsed ? isFetching : v1Loading),
    [isFetching, isV2SearchUsed, v1Loading],
  );

  const options = useMemo(() => formatTransactionOptions(data), [data]);

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const cachePrefetch = useCallback(
    (newValues: TxnEventExternalId[]) => {
      if (!isV2SearchUsed) return;
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((i) => {
        if (prevSet.has(i)) return;
        cacheCgdo({
          object_type: 'txn_event',
          hashed_id: computeDlHashIdForCgdo(orgId, String(i)),
        });
      });
    },
    [cacheCgdo, isV2SearchUsed, orgId, value],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadEventsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;
      if (isV2SearchUsed) {
        const payload: TransactionSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: [
            {
              phrases: missingValues.map(String),
              field: TransactionEventSearchOptions.EXTERNAL_ID,
              match_type: MatchType.EXACT,
            },
          ],
        };

        queryClient.prefetchQuery({
          queryKey: TRANSACTION_QUERY_KEYS.getTransactionSearch(payload),
          queryFn: () => searchTransaction(payload),
        });

        setNewPayload(payload);
      } else {
        missingValues.map((i) =>
          dispatch(
            txnEventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: String(i),
              type: 'external_id',
            }),
          ),
        );
      }
    }
  }, [
    dispatch,
    hasReadEventsPermissions,
    isV2SearchUsed,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        if (isV2SearchUsed) {
          setNewPayload({
            offset: OFFSET_DEFAULT,
            limit: INFINITE_SCROLL_LIMIT,
            search_conditions: [
              {
                phrases: [query],
                field: TransactionEventSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          });
        } else {
          dispatch(
            txnEventsSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: query || '',
              type: 'external_id',
            }),
          );
        }
      }
    },
    [dispatch, isV2SearchUsed],
  );

  if (!hasReadEventsPermissions) {
    return null;
  }

  let noOptionsText = 'Type to search or paste a list of Transaction IDs';
  if (isV2SearchUsed && isV2Error) {
    noOptionsText = 'Search failed. Please try again.';
  } else if (searchValue) {
    noOptionsText = 'No results found';
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<TxnEventExternalId, TClearable>,
    | 'loading'
    | 'onExactSearch'
    | 'onSearch'
    | 'options'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading,
    onExactSearch: isV2SearchUsed
      ? async (searchTerms: string[]) => {
          const payload: TransactionSearchPayload = {
            offset: OFFSET_DEFAULT,
            limit: searchTerms.length,
            search_conditions: [
              {
                phrases: searchTerms.map(String),
                field: TransactionEventSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          };

          await queryClient.prefetchQuery({
            gcTime: Infinity,
            queryKey: TRANSACTION_QUERY_KEYS.getTransactionSearch(payload),
            queryFn: () => searchTransaction(payload),
          });
          refreshTransactions();
        }
      : undefined,
    onSearch,
    options,
    startIcon: <IconSearch />,
    noOptionsText,
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<TxnEventExternalId, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange?.(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<TxnEventExternalId, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        onChange?.(newValue, e);
      }}
    />
  );
};
